import React, {useState} from 'react';
import {Box} from '@mui/material';
import {Button, Divider, Typography} from '@mui/material';
import MutationLoadingButton from 'components/MutationLoadingButton';
import IssuesList from 'components/IssuesList';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';

// TODO: displayIssues input prop
const EditTemporalAttribute = ({
    ComponentEditDraft,
    componentEditDraftProps = {},
    ComponentDisplayDraft,
    ComponentDisplayActive,
    ComponentDisplayHistory,

    attributeTypeLabel = 'Attribute',
    draft,
    active,
    history = [],
    parentAllowsActivation,
    journeyStep,

    mutationCreateDraft,
    mutationUpdateDraft = null,
    mutationDeleteDraft,
    mutationActivateDraft

}) => {

    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [showEditDraft, setShowEditDraft] = useState(  false );

    // TODO: do these handlers need to be async if the mutation is async?
    const createDraft = () => {
        if ( mutationCreateDraft ){
            mutationCreateDraft.mutate();
        }
        else {
            console.log('createDraftMutation not provided');
        }
    };

    // TODO: can we use an update mutation and let the EditDraft component just provide the payload for the mutation?
    // Pros:
    //  - error handling and display can happen in the this component
    // Cons:
    //  - prevents the EditDraft component from custom behavior around save / update.
    // Long term we could provide the option

    const deleteDraft = () => {
        if ( mutationDeleteDraft ){
            confirm({
                title: 'Discard Changes',
                description: 'Are you sure you want to discard all changes? This action cannot be undone.',
                confirmationText: 'Discard',
                cancellationText: 'Cancel',
                dialogProps: {
                    sx: {
                        zIndex: 1450,
                        '& .MuiDialog-paper': {
                            zIndex: (theme) => 1450 + 1,
                        }
                    },
                }
            }).then(() => {
                mutationDeleteDraft.mutate();
            }).catch(() => {
                // User cancelled, do nothing
            });
        }
        else {
            console.log('deleteDraftMutation not provided');
        }
    };

    const activateDraft= async () => {
        if ( mutationActivateDraft ){
            confirm({
                title: 'Activate Changes',
                description: 'Are you sure you want to activate these changes? This will make them live.',
                confirmationText: 'Activate',
                cancellationText: 'Cancel',
                dialogProps: {
                    sx: {
                        zIndex: 1450,
                        '& .MuiDialog-paper': {
                            zIndex: (theme) => 1450 + 1,
                        }
                    },
                }
            }).then(() => {
                mutationActivateDraft.mutate();
            }).catch(() => {
                // User cancelled, do nothing
            });
        }
        else {
            console.log('activateDraftMutation not provided');
        }
    };

    const handleEditStart = () => {
        setShowEditDraft(true);
    };

    const handleSaveDraft = (mutation_payload) => {
        if (mutationUpdateDraft){
            mutationUpdateDraft.mutate(mutation_payload,
                { onSuccess : (data) => {
                    setShowEditDraft(false);
                    enqueueSnackbar('Draft saved', {variant: 'success'});
                    // Invalidate journey canvas to reload after draft changes
                    console.log('journeyStep on save draft', journeyStep);
                    if (journeyStep?.flow_id) {
                        // queryClient.invalidateQueries(['journeys', journeyStep.flow_id, 'adjacencyList', 'draft']);
                        queryClient.invalidateQueries(['journeys', journeyStep.flow_id]);
                    }
                }
                });
        }
    };

    // Needed now since the EditComponent manages its own cancel/save events.
    // If we changed to the editComponent having an onSubmit/onCancel set of event handlers,
    // we could use those to set the showEditDraft state
    const handleChangeEditState = (state) => {
        console.log('BranchFilterSidebar handleSwitchEditState', state);
        if(state === 'cancel') {
            setShowEditDraft(false);
        }
        else if(state === 'success') {
            setShowEditDraft(false);
        }
        else if(state === 'editing') {
            setShowEditDraft(true);
        }
    };


    // const handleCancel = () => {
    //     setShowEditDraft(false);
    // }

    // const statusBadge = flowStatesHelper(journeyStep?.status, journeyStep?.has_changes, 'medium');

    const manageActiveDraft =
        <>
            {draft &&
                <>
                    { showEditDraft
                        ? <>
                            <ComponentEditDraft
                                editObj={draft}
                                // TODO: passi in the mutation instead of the handleSaveDraft ?
                                onSave={handleSaveDraft}
                                onChangeEditState={handleChangeEditState}
                                {...componentEditDraftProps} />
                        </>
                        : <Box>
                            <IssuesList 
                                label="Activation Status" 
                                issues={draft.activation_issues} 
                                enableStatusToggle={true}
                            />
                            <ComponentDisplayDraft draftObj={draft} />
                            <Box sx={{ mt: 1, mb: 2 }}>
                                {/* Edit button on its own row */}
                                <Box display='flex' justifyContent='center' sx={{ mb: 1 }}>
                                    <Button className='new_primary_button' onClick={handleEditStart}>
                                        Edit
                                    </Button>
                                </Box>
                                
                                {/* Discard and Activate buttons on second row - smaller */}
                                {(active || (draft.can_activate && parentAllowsActivation)) && (
                                    <Box display='flex' justifyContent='center' gap={1} flexWrap='wrap'>
                                        {active && (
                                            <MutationLoadingButton 
                                                className='new_primary_button' 
                                                onClick={deleteDraft} 
                                                mutation={mutationDeleteDraft}
                                                size='small'
                                            >
                                                Discard changes
                                            </MutationLoadingButton>
                                        )}
                                        {(draft.can_activate && parentAllowsActivation) && (
                                            <MutationLoadingButton 
                                                className='new_primary_button' 
                                                onClick={activateDraft} 
                                                mutation={mutationActivateDraft}
                                                size='small'
                                            >
                                                Activate changes
                                            </MutationLoadingButton>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    }
                </>
            }
            {active !== null &&
                <>
                    {draft == null &&
                        <MutationLoadingButton className='new_primary_button' onClick={createDraft} mutation={mutationCreateDraft}>create new version to edit</MutationLoadingButton>
                    }
                    <Divider/>
                    <ComponentDisplayActive activeObj={active} />
                    <Divider/>
                </>
            }
            {history.length > 0 &&
                <Box>
                    <Typography >{attributeTypeLabel} History</Typography>
                    {history.map( (version) => <ComponentDisplayHistory historyObj={version}/>
                    )}
                </Box>
            }

        </>;

    return manageActiveDraft;
};



export default EditTemporalAttribute;
